import { useState } from 'react';
import { cn } from '@/utils';
import { FaUserFriends } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import GameSessionModal from '@/components/common/GameSessionModal';
import Loader from '@/components/common/Loader';
import { useCasinoGameplay } from '@/hooks/useCasinoGameplay';

import 'swiper/css';
import 'swiper/css/free-mode';

const fallbackGames = [
  {
    name: 'coin Toss',
    image: '/images/bottom-nav-casino/coinToss-enter.webp',
    gameId: 493,
  },
  {
    name: 'Andar Bahar',
    image: '/images/bottom-nav-casino/andarbahar-enter.webp',
    gameId: 765,
  },
  {
    name: '7up7down',
    image: '/images/bottom-nav-casino/7up7down-enter.webp',
    gameId: 256,
  },
  {
    name: 'Rocket',
    image: '/images/bottom-nav-casino/rocket-enter.webp',
    gameId: 488,
  },
];

export default function BottomCasinoLauncher() {
  const [open, setOpen] = useState(false);
  const { activeGameId, sessionUrl, isModalOpen, sessionLoading, handleGameClick, closeModal } =
    useCasinoGameplay();

  return (
    <>
      {sessionLoading && (
        <Loader className="fixed z-[70] flex items-center justify-center bg-black/50" />
      )}

      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open casino games"
        className={cn(
          'max-[480px]:left-3.5 fixed bottom-[6rem] left-[calc(50%-14.5rem)] z-40 flex size-[4.5rem] items-end justify-center overflow-visible'
        )}
      >
        <img
          src="/images/games.svg"
          alt=""
          className="absolute inset-0 h-full w-full object-contain drop-shadow-xl"
        />
      </button>

      {open && (
        <div className="fixed inset-x-0 bottom-0 z-[55] flex justify-center">
          <div className="w-full max-w-[30rem] animate-slide-up overflow-hidden rounded-t-[1.4rem] bg-black">
            <div className="relative h-[2.5rem] flex items-center justify-between bg-black">
              <img src="/images/icon-title.webp" alt="" className="h-full" />
              <button type="button" onClick={() => setOpen(false)} className="h-full">
                <img src="/images/icon-close.webp" alt="" className="h-full" />
              </button>
            </div>

            <div className="max-h-[14rem] overflow-hidden bg-[url('/images/bottom-nav-casino/lobby-bg.webp')] bg-cover bg-center px-1 py-6">
              <Swiper
                modules={[FreeMode]}
                slidesPerView={3}
                spaceBetween={8}
                freeMode
                className="w-full"
              >
                {fallbackGames.map((game, index) => (
                  <SwiperSlide key={game.name} className="!h-auto">
                    <CasinoLauncherCard
                      name={game.name}
                      image={game.image}
                      count={index === 2 ? 844 : 834}
                      disabled={sessionLoading}
                      isLoading={activeGameId === game.gameId}
                      onClick={() => handleGameClick({ id: game.gameId })}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      )}

      <GameSessionModal url={sessionUrl} isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
}

function CasinoLauncherCard({
  name,
  image,
  count,
  disabled,
  isLoading,
  onClick,
}: {
  name: string;
  image?: string;
  count: number;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-w-0 flex-col overflow-hidden rounded-[0.4rem] bg-white text-left',
        disabled && 'cursor-not-allowed opacity-70'
      )}
    >
      <div className="relative aspect-[1 /1.08] w-full rounded-t-[0.4rem] p-1">
        <img
          src={image || '/images/casino-card-placeholder.webp'}
          alt={name}
          className="h-full w-full object-cover rounded-2xl"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = '/images/casino-card-placeholder.webp';
          }}
        />

        <span
          className="
        absolute bottom-0 left-0 translate-y-1/2
        flex h-[1.65rem] items-center gap-1
        rounded-full bg-black
        pl-0.5 pr-1.5 text-[0.8rem] border-[0.2rem] border-white
        leading-none text-primary
      "
        >
          <span className="flex size-[1.15rem] items-center justify-center rounded-full bg-gradient-to-r from-amber-300 to-amber-400">
            <FaUserFriends className={'text-black'} />
          </span>

          {count}
        </span>

        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center rounded-t-[0.4rem] bg-black/45 text-[0.75rem] font-bold text-primary">
            Launching...
          </span>
        )}
      </div>

      <div
        className="
      flex min-h-[2.75rem] w-full items-start
      justify-center px-1.5 pt-3
      text-center text-[0.875rem]
      font-bold leading-tight text-black
    "
      >
        <span className="w-full truncate">{name}</span>
      </div>
    </button>
  );
}
